/**
 * Shared survey/group editor orchestration under the SAVE-ALWAYS model.
 *
 * The backend persists every edit and returns a structural `report`; activation is the gate, not
 * the save. This hook centralizes that contract for both SurveyDetails and SurveyGroupDetails so
 * the two screens behave identically:
 *
 *   - `saveQuestions` ALWAYS persists the edit. A clean or merely-broken survey saves silently and
 *     the returned report drives the per-question issue badges. The ONE case that interrupts is a
 *     destructive edit to a survey with live responses, which the backend answers with a 422
 *     (typed-confirmation) — surfaced here as a confirm modal, not a lost edit.
 *   - `report` / `issuesByQuestion` / `errorCount` / `canActivate` let the editor flag offending
 *     questions inline and disable activation until the structure is clean.
 *   - `goToQuestion` only moves focus — it NEVER discards pending edits (the old gated-save hook
 *     reset editor state here, silently dropping the user's work).
 *
 * The backend is the authoritative validator; this hook only drives the UX around it.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  SurveyValidationError,
  errorCount as countErrors,
  issuesByQuestionId,
  type ValidationReport,
  type ValidationIssue,
} from '../../utils/surveyValidation';

export interface EditorPending {
  questions: unknown[];
  blocks?: unknown;
  [key: string]: unknown;
}

export interface SurveyEditorOptions<P extends EditorPending, R> {
  /** Dry-run validation (POST .../validate). Optionally apply auto-fixes server-side. */
  validate: (
    questions: unknown[],
    blocks: unknown,
    opts?: { applyAutoFixes?: boolean; fixes?: Record<string, unknown>[] }
  ) => Promise<{ report: ValidationReport; fixed_questions?: unknown[] }>;
  /** Persist the questions (PUT). Throws SurveyValidationError only on the typed-confirmation 422. */
  save: (pending: P, acknowledgeWarnings: boolean) => Promise<R>;
  /** Apply a successful save result to component state (e.g. setSurvey / setGroup). */
  applyResult: (result: R) => void;
  /** Pull the structural report out of a save result (it rides along in the success body). */
  extractReport: (result: R) => ValidationReport | null | undefined;
  /** Surface a user-facing error message. */
  onError?: (message: string) => void;
}

export interface SurveyEditor<P extends EditorPending> {
  /** Latest structural report (from a save or an explicit refresh); null until first known. */
  report: ValidationReport | null;
  issuesByQuestion: Map<string, ValidationIssue[]>;
  errorCount: number;
  /** A survey may only go live when it has zero structural errors. */
  canActivate: boolean;
  busy: boolean;
  focusQuestionId: string | null;
  clearFocusQuestion: () => void;
  /** Pending typed-confirmation report (destructive live-response edit), else null. */
  confirmReport: ValidationReport | null;
  /** Entry point from the editor's onQuestionsUpdated: persist immediately, store the report. */
  saveQuestions: (pending: P) => Promise<void>;
  /** Replace the report directly (on-load population, or from an activation 422). */
  setReport: (report: ValidationReport | null) => void;
  /** Typed-confirmation modal actions. */
  confirmSave: () => Promise<void>;
  fixAllAndConfirm: () => Promise<void>;
  autoFix: (issue: ValidationIssue) => Promise<void>;
  goToQuestion: (questionId: string) => void;
  cancelConfirm: () => void;
}

export function useSurveyEditor<P extends EditorPending, R>(
  options: SurveyEditorOptions<P, R>
): SurveyEditor<P> {
  const { validate, save, applyResult, extractReport, onError } = options;

  const [report, setReport] = useState<ValidationReport | null>(null);
  const [confirm, setConfirm] = useState<{ pending: P; report: ValidationReport } | null>(null);
  const [busy, setBusy] = useState(false);
  const [focusQuestionId, setFocusQuestionId] = useState<string | null>(null);

  // Persist; a typed-confirmation 422 routes into the confirm modal instead of failing opaquely.
  const persist = useCallback(
    async (pending: P, acknowledge: boolean): Promise<boolean> => {
      const result = await save(pending, acknowledge);
      applyResult(result);
      setReport(extractReport(result) ?? null);
      return true;
    },
    [save, applyResult, extractReport]
  );

  const saveQuestions = useCallback(
    async (pending: P) => {
      try {
        setBusy(true);
        await persist(pending, false);
        setConfirm(null);
      } catch (err) {
        if (err instanceof SurveyValidationError) {
          setConfirm({ pending, report: err.report });
          return;
        }
        console.error('Save failed:', err);
        onError?.('Failed to save questions. Please try again.');
      } finally {
        setBusy(false);
      }
    },
    [persist, onError]
  );

  const confirmSave = useCallback(async () => {
    if (!confirm) return;
    try {
      setBusy(true);
      await persist(confirm.pending, true);
      setConfirm(null);
    } catch (err) {
      console.error('Confirm-save failed:', err);
      onError?.('Failed to save questions. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [confirm, persist, onError]);

  const fixAllAndConfirm = useCallback(async () => {
    if (!confirm) return;
    try {
      setBusy(true);
      const { fixed_questions } = await validate(
        confirm.pending.questions, confirm.pending.blocks, { applyAutoFixes: true }
      );
      const pending = {
        ...confirm.pending,
        questions: (fixed_questions as unknown[]) ?? confirm.pending.questions,
      };
      await persist(pending, true);
      setConfirm(null);
    } catch (err) {
      console.error('Fix-all failed:', err);
      onError?.('Failed to fix and save. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [confirm, validate, persist, onError]);

  const autoFix = useCallback(
    async (issue: ValidationIssue) => {
      if (!confirm || !issue.fix) return;
      try {
        setBusy(true);
        const { fixed_questions } = await validate(
          confirm.pending.questions, confirm.pending.blocks,
          { fixes: [issue.fix as Record<string, unknown>] }
        );
        const pending = {
          ...confirm.pending,
          questions: (fixed_questions as unknown[]) ?? confirm.pending.questions,
        };
        await persist(pending, true);
        setConfirm(null);
      } catch (err) {
        console.error('Auto-fix failed:', err);
        onError?.('Failed to apply fix. Please try again.');
      } finally {
        setBusy(false);
      }
    },
    [confirm, validate, persist, onError]
  );

  const goToQuestion = useCallback((questionId: string) => {
    // Move focus only — pending edits are the editor's own source of truth and must survive this.
    setConfirm(null);
    setFocusQuestionId(questionId);
  }, []);

  const cancelConfirm = useCallback(() => setConfirm(null), []);
  const clearFocusQuestion = useCallback(() => setFocusQuestionId(null), []);

  const issuesByQuestion = useMemo(() => issuesByQuestionId(report), [report]);
  const errCount = useMemo(() => countErrors(report), [report]);

  return {
    report,
    issuesByQuestion,
    errorCount: errCount,
    canActivate: errCount === 0,
    busy,
    focusQuestionId,
    clearFocusQuestion,
    confirmReport: confirm?.report ?? null,
    saveQuestions,
    setReport,
    confirmSave,
    fixAllAndConfirm,
    autoFix,
    goToQuestion,
    cancelConfirm,
  };
}
